import { FC, useEffect } from 'react'
import { SpaceBetween } from '@cloudscape-design/components'
import FormInput from '../Input'
import { useForm } from 'react-hook-form'
import FormSelect from '../Select'
import { useGenericTagsManagementFormStore } from '../../../stores/tags/genericTagsManagementFormStore'
import { usegcpProjectCreationFormStore } from '../../../stores/gcpProjects/newgcpProjectCreationFormStore';
import useApprovedGcpProjectsWithTags from '../../../hooks/query/useApprovedGcpProjectsWithTags'
import { findApprovedGcpProjectAliasConflict } from '../../../helper/gcpAliasValidation'
import CONSTANT from '../../../constants'
const { COST_TYPE_LIST, PLATFORM_LIST, DATA_CLASSIFICATION_LIST } = CONSTANT

 
const GenericTagsNew: FC = () => {

    const {
        formValues,
        actions: { setFormValues, setFormValidity }
    } = useGenericTagsManagementFormStore()

	const { formValues: projectformValues } = usegcpProjectCreationFormStore();
	const gcpProjectType = projectformValues.gcpProjectType ?? '';
	const distributionListKey = `${gcpProjectType}ProjectViewerDistributionList`;
	const projectViewerDistributionList =
		typeof projectformValues[distributionListKey] === 'string'
			? projectformValues[distributionListKey]
			: '';
	const {
		data: approvedGcpProjects,
		isLoading: isApprovedProjectsLoading,
		isError: isApprovedProjectsError
	} = useApprovedGcpProjectsWithTags()
    const {
        control,
        formState: { isValid },
        setValue,
        watch
    } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: formValues,
        shouldUseNativeValidation: true
    })

	const watchedSystemAlias = watch('systemAlias')
	const isAliasValidationPending = Boolean(
		watchedSystemAlias && gcpProjectType && isApprovedProjectsLoading
	)

	const validateSystemAlias = (value: string): boolean | string => {
		if (!value || !gcpProjectType) return true
		if (isApprovedProjectsLoading) return true
		if (isApprovedProjectsError) {
			return 'Unable to validate alias uniqueness right now'
		}

		const conflict = findApprovedGcpProjectAliasConflict(
			approvedGcpProjects,
			{
				alias: value,
				gcpProjectType
			}
		)

		if (!conflict) return true

		return `Platform Alias already exists for an approved GCP Project request in ${conflict.environmentLabel}.`
	}

	const hasEligibleSystemAliasForExternalValidation = /^[A-Za-z][A-Za-z]{4}$/.test(
		watchedSystemAlias?.trim?.() ?? ''
	)

	let systemAliasValidationMessage: string | undefined

	if (
		watchedSystemAlias &&
		gcpProjectType &&
		hasEligibleSystemAliasForExternalValidation &&
		!isApprovedProjectsLoading
	) {
		if (isApprovedProjectsError) {
			systemAliasValidationMessage =
				'Unable to validate alias uniqueness right now'
		} else {
			const conflict = findApprovedGcpProjectAliasConflict(approvedGcpProjects, {
				alias: watchedSystemAlias,
				gcpProjectType
			})

			if (conflict) {
				systemAliasValidationMessage = `Platform Alias already exists for an approved GCP Project request in ${conflict.environmentLabel}.`
			}
		}
	}
 
    useEffect(() => {
        setFormValidity(
			isValid && !isAliasValidationPending && !systemAliasValidationMessage
		)
    }, [isAliasValidationPending, isValid, setFormValidity, systemAliasValidationMessage])
 
    useEffect(() => {
        if (formValues?.fundingSourceType && formValues.fundingSourceType?.value === 'opex') {
            setValue('WBSCode', '', { shouldValidate: true })
			setFormValues('WBSCode', '')
        } else if (formValues?.fundingSourceType && formValues.fundingSourceType?.value === 'capex') {
            setValue('costCenter', '', { shouldValidate: true })
			setFormValues('costCenter', '')
        }
    }, [formValues.fundingSourceType, setValue, setFormValues])

	useEffect(() => {
		if (formValues.projectName !== undefined && gcpProjectType === '') {
			setValue('projectName', formValues.projectName)
		} else {
			const groupValue = projectViewerDistributionList.split('-')[2] || ''
			const groupObject = { label: groupValue, value: groupValue }
			let projectType = '';
			if (gcpProjectType === 'Develop') {
				projectType = 'dev';
			} else if (gcpProjectType === 'Staging') {
				projectType = 'stg';
			} else if (gcpProjectType === 'Production') {
				projectType = 'prod';
			} else if (gcpProjectType === 'Sandbox') {
				projectType = 'sbx';
			}
			const projectNameValue = `glo-${groupValue}-${formValues.systemAlias}-project-${projectType}`;
			setValue('projectName', projectNameValue);
			setFormValues('projectName', projectNameValue);
			setFormValues('group', groupObject)
		}
	}, [
		formValues.group?.value,
		formValues.systemAlias,
		formValues?.projectName,
		gcpProjectType,
		projectViewerDistributionList,
		setValue,
		setFormValues
	]);


	useEffect(() => {
		setValue('platformOwner', formValues.platformOwner)
		setValue('platformName', formValues.platformName)
		setValue('projectName', formValues.projectName)
		setValue('group', formValues.group)
		setValue('fundingSourceType', formValues.fundingSourceType)
		setValue('costCenter', formValues.costCenter)
		setValue('WBSCode', formValues.WBSCode)
	}, [formValues, setValue])

    return (
		<SpaceBetween direction="vertical" size="s">
			<SpaceBetween direction="vertical" size="m">
				<FormSelect
					name="platformName"
					label="Platform Name"
					description="Select the platform that owns or operates this GCP project."
					control={control}
					rules={{
						required: 'Please Select Option'
					}}
					setFormValues={setFormValues}
					options={PLATFORM_LIST}
					placeholder="Select Platform "
				/>
				<FormInput
					name="platformOwner"
					label="Platform Owner"
					description="Enter the responsible platform owner in lowercase kebab-case, e.g., juan-dela-cruz."
					type="text"
					rules={{
						required: 'Please enter a platform owner',
						pattern: {
							value: /^[a-z]+(?:-[a-z]+)*$/,
							message: "Platform owner should be in lowercase kebab case"
						}
					}}
					placeholder="juan-cruz"
					control={control}
					setFormValues={setFormValues}
				/>
				<FormInput
					name="systemAlias"
					label="Platform Alias"
					description="Enter the five-letter platform alias for the generated GCP project name."
					type="text"
					placeholder="Example: hipms, namax"
					control={control}
					rules={{
						required: 'System alias should be 5 letters, Example: hipms',
						pattern: {
							value: /^[A-Za-z][A-Za-z]{4,}$/,
							message: 'System alias should be 5 letters, Example: hipms'
						},
						minLength: {
							value: 5,
							message: 'System alias should be 5 letters, Example: hipms'
						},
						maxLength: {
							value: 5,
							message: 'System alias should be 5 letters, Example: namax'
						},
						validate: validateSystemAlias
					}}
					errorText={systemAliasValidationMessage}
					setFormValues={setFormValues}
				/>
				<FormInput
					name="projectName"
					label="GCP Project Name"
					description="Auto-generated from the selected project type and platform alias."
					type="text"
					placeholder="Generated after project type and platform alias are set"
					control={control}
					setFormValues={setFormValues}
					disabled
				/>
				{/* <FormSelect
					label="Globe Group"
					name="group"
					control={control}
					rules={{
						required: 'Please Select Option'
					}}
					setFormValues={setFormValues}
					options={PORTFOLIO_LIST}
					placeholder="Select Group"
				/> */}
				{/* <br /> */}
				<FormSelect
					label="Data Classification"
					name="dataClassification"
					description="Select the data sensitivity level for this project."
					control={control}
					rules={{
						required: 'Please Select Option'
					}}
					setFormValues={setFormValues}
					options={DATA_CLASSIFICATION_LIST}
					placeholder="Select Data Classification"
				/>
			</SpaceBetween>
			<SpaceBetween direction="vertical" size="m">
				<FormSelect
					label="Funding Source"
					name="fundingSourceType"
					description="Choose whether to charge project costs to a Cost Center or a WBS Code."
					control={control}
					rules={{
						required: 'Please Select Option'
					}}
					setFormValues={setFormValues}
					options={COST_TYPE_LIST}
					placeholder="Select Funding Source Type"
				/>
				{/* <br/> */}
				{formValues?.fundingSourceType?.value === 'capex' && (
					<FormInput
						name="WBSCode"
						label="WBS Code"
						description="Enter in all caps with at least three parts separated by dashes, e.g., PSD-OMN-21 or C-D-PR02-JE67CT-89-HW-01-GL-BE."
						type="text"
						control={control}
						rules={{
							pattern: {
								value: /^[A-Z0-9]+(?:-[A-Z0-9]+){2,}$/,
								message: 'Enter in all caps with at least three parts separated by dashes, e.g., PSD-OMN-21 or C-D-PR02-JE67CT-89-HW-01-GL-BE.'
							},
							minLength: {
								value: 3,
								message: 'Minimum length should be 3'
							}
						}}
						setFormValues={setFormValues}
						placeholder="Example: ISG-PROJECTA-23, EDO-PROJECTC-24-SW-01, A-B-PR01-JE23CT-45, E-F-PR03-JE45CT-91-BE-GL"
					/>
				)}
				{formValues?.fundingSourceType?.value === 'opex' && (
					<FormInput
						name="costCenter"
						label="Cost Center"
						description="Please enter all in caps, e.g., OP20-G, ISG24-G, PSD-OMN-21"
						type="text"
						control={control}
						rules={{
							pattern: {
								value: /^[A-Z0-9]{1,7}-[A-Z0-9]{1,7}$/,
								message: 'Please enter all in caps, e.g., OP20-G, ISG24-G, PSD-OMN-21'
							},
							minLength: {
								value: 3,
								message: 'Minimum length should be 3'
							}
						}}
						setFormValues={setFormValues}
						placeholder="Example: ISG17-A, OP20-G, MP-CIV-20, HTT7-B,ISG01,PSD-TECCF/19"
					/>
				)}
			</SpaceBetween>
		</SpaceBetween>
	)
}
 
export default GenericTagsNew
